import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import CgmModulePageLayout from './layout/CgmModulePageLayout';
import './CgmFilterToolbar.css';
import './CgmModulePage.css';
import {
  CGM_MONTH_OPTIONS,
  getCurrentMonthYear,
  filterByMonthYear,
  buildYearOptions,
} from '../utils/cgmMonthYearFilter';
import { authJsonHeaders } from '../utils/authHeaders';

const { month: DEFAULT_FILTER_MONTH, year: DEFAULT_FILTER_YEAR } = getCurrentMonthYear();

export default function AnciensRetraitsPage() {
  const navigate = useNavigate();
  const { user, isCaissier, activeCabinet } = useAuth();
  const cabinetLabel = activeCabinet?.is_default || Number(activeCabinet?.id) === 1 ? 'CGM1' : 'CGM2';
  
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [filterMonth, setFilterMonth] = useState(DEFAULT_FILTER_MONTH);
  const [filterYear, setFilterYear] = useState(DEFAULT_FILTER_YEAR);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState(''); // '' | 'bureau' | 'autre'

  // Modal d'édition
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOperation, setEditingOperation] = useState(null);
  const [editFormData, setEditFormData] = useState({
    montant: '',
    commentaire: '',
    date_retrait: '',
    retrait_type: 'autre',
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch('/api/caisse-cgm', {
        headers: authJsonHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        setOperations(data.operations || []);
      } else {
        setError(data.error || 'Erreur lors du chargement des retraits');
      }
    } catch (err) {
      setError(err.message || 'Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeCabinet?.id]);

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(montant || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const str = String(dateStr).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
      const [y, m, d] = str.slice(0, 10).split('-');
      return `${d}/${m}/${y}`;
    }
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const yearOptions = useMemo(
    () => buildYearOptions(operations, (op) => op.date || op.created_at),
    [operations]
  );

  const getRetraitTypeKey = (op) => {
    const t = String(op?.type_retrait || op?.retrait_type || '').toLowerCase();
    if (t === 'bureau' || Number(op?.type_retrait) === 1 || op?.is_bureau) return 'bureau';
    if (t === 'inventaire') return 'inventaire';
    if (t === 'autre') return 'autre';
    if (op?.source_type === 'internal_expense_bureau') return 'bureau';
    if (op?.source_type === 'internal_expense_autre') return 'autre';
    return 'autre';
  };

  const filteredOperations = useMemo(() => {
    const q = String(searchQuery || '').trim().toLowerCase();
    let list = q
      ? [...operations]
      : filterByMonthYear(operations, filterMonth, filterYear, (op) => op.date || op.created_at);

    if (filterType) {
      list = list.filter((op) => getRetraitTypeKey(op) === filterType);
    }

    if (!q) return list;

    const qDigits = q.replace(/\s/g, '').replace(',', '.');
    return list.filter((op) => {
      const commentaire = String(op.commentaire || '').toLowerCase();
      const typeLabel = getRetraitTypeKey(op);
      const montantStr = String(op.montant ?? '').toLowerCase();
      const montantFmt = formatMontant(op.montant).toLowerCase().replace(/\s/g, '');
      const dateStr = formatDate(op.date || op.created_at).toLowerCase();
      const hay = `${commentaire} ${typeLabel} ${montantStr} ${dateStr}`;
      if (hay.includes(q)) return true;
      if (qDigits && (montantStr.includes(qDigits) || montantFmt.includes(qDigits.replace('.', ',')))) {
        return true;
      }
      return false;
    });
  }, [operations, filterMonth, filterYear, filterType, searchQuery]);

  const totalFiltre = useMemo(() => {
    return filteredOperations.reduce((sum, op) => sum + (parseFloat(op.montant) || 0), 0);
  }, [filteredOperations]);

  const handleOpenEdit = (op) => {
    setEditingOperation(op);
    let dateRetrait = new Date().toISOString().slice(0, 10);
    if (op.date) dateRetrait = String(op.date).slice(0, 10);
    else if (op.created_at) {
      const d = new Date(op.created_at);
      dateRetrait = !isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : dateRetrait;
    }

    setEditFormData({
      montant: Math.abs(parseFloat(op.montant) || 0),
      commentaire: op.commentaire || '',
      date_retrait: dateRetrait,
      retrait_type: getRetraitTypeKey(op),
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingOperation) return;
    const montantNum = Math.abs(parseFloat(editFormData.montant) || 0);
    if (montantNum <= 0) {
      alert('Le montant doit être supérieur à 0');
      return;
    }

    try {
      const response = await fetch(`/api/caisse-cgm/${editingOperation.id}`, {
        method: 'PUT',
        headers: authJsonHeaders(),
        body: JSON.stringify({
          montant: montantNum,
          date: editFormData.date_retrait,
          date_retrait: editFormData.date_retrait,
          commentaire: editFormData.commentaire,
          note: editFormData.commentaire,
          retrait_type: editFormData.retrait_type,
          type_retrait: editFormData.retrait_type,
          is_bureau: editFormData.retrait_type === 'bureau' ? 1 : 0,
        }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setSuccessMessage('Opération modifiée avec succès');
        setShowEditModal(false);
        setEditingOperation(null);
        fetchData();
        window.dispatchEvent?.(new CustomEvent('caisse-updated'));
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        alert(data.error || data.message || 'Erreur lors de la modification');
      }
    } catch (err) {
      alert(err.message || 'Erreur réseau');
    }
  };

  const handleDelete = async (op) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ce retrait de ${formatMontant(op.montant)} TND ?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/caisse-cgm/${op.id}`, {
        method: 'DELETE',
        headers: authJsonHeaders(),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccessMessage('Retrait supprimé avec succès');
        fetchData();
        window.dispatchEvent?.(new CustomEvent('caisse-updated'));
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        alert(data.error || data.message || 'Erreur lors de la suppression');
      }
    } catch (err) {
      alert(err.message || 'Erreur réseau');
    }
  };

  return (
    <CgmModulePageLayout
      crumb="Comptabilité CGM · Caisse · Anciens Retraits"
      title={`Anciens Retraits · ${cabinetLabel}`}
      backTo="/caisse-cgm"
      wide
    >
      <div className="cgm-list-toolbar mb-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => navigate('/caisse-cgm')}
            >
              <i className="fas fa-arrow-left me-1" /> Retour à la Caisse
            </button>
            <h5 className="mb-0 text-dark fw-bold">
              <i className="fas fa-history me-2 text-secondary" />
              Historique des anciens retraits
            </h5>
          </div>
          <div className="badge bg-danger fs-6 py-2 px-3">
            Total filtré : -{formatMontant(totalFiltre)} TND
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="fas fa-check-circle me-2"></i>
          {successMessage}
          <button type="button" className="btn-close" onClick={() => setSuccessMessage('')}></button>
        </div>
      )}

      {/* Barre de filtre */}
      <div className="cgm-filter-bar mb-3">
        <div className="cgm-filter-group">
          <label className="cgm-filter-label" htmlFor="filterMonth">Mois</label>
          <select
            id="filterMonth"
            className="cgm-filter-select"
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
          >
            {CGM_MONTH_OPTIONS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        <div className="cgm-filter-group">
          <label className="cgm-filter-label" htmlFor="filterYear">Année</label>
          <select
            id="filterYear"
            className="cgm-filter-select"
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
          >
            {yearOptions.map((y) => (
              <option key={y.value} value={y.value}>{y.label}</option>
            ))}
          </select>
        </div>

        <div className="cgm-filter-group">
          <label className="cgm-filter-label" htmlFor="filterType">Type</label>
          <select
            id="filterType"
            className="cgm-filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">Tous</option>
            <option value="inventaire">Inventaire</option>
            <option value="bureau">Bureau</option>
            <option value="autre">Autre</option>
          </select>
        </div>

        <div className="cgm-filter-group cgm-filter-group--grow">
          <label className="cgm-filter-label" htmlFor="searchQuery">Recherche</label>
          <input
            id="searchQuery"
            type="text"
            className="cgm-filter-input"
            placeholder="Texte, commentaire, montant..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Tableau des retraits */}
      <div className="cgm-table-card">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: '120px' }}>Date</th>
                <th style={{ width: '140px' }}>Type</th>
                <th style={{ width: '150px' }} className="text-end">Montant</th>
                <th>Commentaire</th>
                <th style={{ width: '100px' }} className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center py-4 text-muted">
                    <div className="spinner-border spinner-border-sm me-2" role="status" />
                    Chargement des retraits...
                  </td>
                </tr>
              ) : filteredOperations.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-4 text-muted">
                    <i className="fas fa-info-circle me-2" />
                    Aucun retrait trouvé pour cette sélection.
                  </td>
                </tr>
              ) : (
                filteredOperations.map((op) => {
                  const typeKey = getRetraitTypeKey(op);
                  const badgeClass = typeKey === 'bureau' ? 'bg-primary' : (typeKey === 'inventaire' ? 'bg-danger' : 'bg-info text-dark');
                  const label = typeKey === 'bureau' ? 'Bureau' : (typeKey === 'inventaire' ? 'Inventaire' : 'Autre');
                  return (
                    <tr key={`${op.source_type || 'op'}_${op.id}`}>
                      <td className="fw-semibold text-secondary">{formatDate(op.date || op.created_at)}</td>
                      <td>
                        <span className={`badge ${badgeClass}`}>
                          {label}
                        </span>
                      </td>
                      <td className="text-end fw-bold text-danger">
                        -{formatMontant(op.montant)} TND
                      </td>
                      <td className="text-truncate" style={{ maxWidth: '400px' }} title={op.commentaire}>
                        {op.commentaire || '-'}
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm me-1 p-1 px-2"
                          title="Modifier"
                          onClick={() => handleOpenEdit(op)}
                        >
                          <i className="fas fa-edit" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm p-1 px-2"
                          title="Supprimer"
                          onClick={() => handleDelete(op)}
                        >
                          <i className="fas fa-trash-alt" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal d'édition */}
      {showEditModal && editingOperation && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0">
              <form onSubmit={handleSaveEdit}>
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title fw-bold">
                    <i className="fas fa-edit me-2" />
                    Modifier le retrait
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setShowEditModal(false)}
                  />
                </div>
                <div className="modal-body p-4">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={editFormData.date_retrait}
                      onChange={(e) => setEditFormData({ ...editFormData, date_retrait: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Montant (TND)</label>
                    <input
                      type="number"
                      step="0.001"
                      min="0.001"
                      className="form-control"
                      value={editFormData.montant}
                      onChange={(e) => setEditFormData({ ...editFormData, montant: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Type</label>
                    <select
                      className="form-select"
                      value={editFormData.retrait_type}
                      onChange={(e) => setEditFormData({ ...editFormData, retrait_type: e.target.value })}
                    >
                      <option value="inventaire">Inventaire</option>
                      <option value="bureau">Bureau</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Commentaire / Note</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editFormData.commentaire}
                      onChange={(e) => setEditFormData({ ...editFormData, commentaire: e.target.value })}
                    />
                  </div>
                </div>
                <div className="modal-footer bg-light">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowEditModal(false)}
                  >
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-primary px-4 fw-bold">
                    Enregistrer
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </CgmModulePageLayout>
  );
}
